/**
 * Voice Memo Waveform
 *
 * Shared waveform display for voice memo drafts and sent messages.
 *
 * Primary responsibilities:
 * - Render compact normalized audio waveform bars
 * - Format voice memo duration labels
 *
 * Dependencies:
 * - lib/utils
 */

'use client'

import { memo, useMemo } from 'react'
import { cn } from '@/lib/utils'

interface VoiceMemoWaveformProps {
  active?: boolean
  className?: string
  samples: number[]
}

const fallbackSamples = [
  0.22, 0.48, 0.36, 0.7, 0.42, 0.56, 0.3, 0.64,
  0.5, 0.38, 0.76, 0.46, 0.58, 0.34, 0.66, 0.4,
  0.52, 0.72, 0.44, 0.6, 0.32, 0.54, 0.68, 0.36
]

function scaleWaveformSamples(samples: number[]) {
  const minSample = Math.min(...samples)
  const maxSample = Math.max(...samples)
  const sampleRange = maxSample - minSample

  if (maxSample >= 0.55 || sampleRange < 0.06) {
    return samples
  }

  const displayFloor = 0.16

  return samples.map((sample) =>
    displayFloor + ((sample - minSample) / sampleRange) * (1 - displayFloor)
  )
}

// --------------

function getVisibleWaveformSamples(samples: number[]) {
  if (samples.length === 0) {
    return fallbackSamples
  }

  return scaleWaveformSamples(samples)
}

// --------------

export function formatVoiceMemoDuration(durationSeconds?: number | null) {
  const totalSeconds =
    typeof durationSeconds === 'number' && Number.isFinite(durationSeconds)
      ? Math.max(0, Math.round(durationSeconds))
      : 0
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// --------------

const VoiceMemoWaveformComponent = ({
  active = false,
  className,
  samples
}: VoiceMemoWaveformProps) => {
  const visibleSamples = useMemo(() => getVisibleWaveformSamples(samples), [samples])
  const bars = useMemo(
    () =>
      visibleSamples.map((sample, index) => ({
        height: Math.max(8, Math.round(sample * 30)),
        key: `${index}-${Math.round(sample * 30)}`
      })),
    [visibleSamples]
  )

  return (
    <div
      aria-hidden="true"
      className={cn('flex h-8 min-w-0 flex-1 items-center gap-0.5 overflow-hidden', className)}
    >
      {bars.map((bar) => (
        <span
          key={bar.key}
          className={cn(
            'bg-primary/60 w-1 shrink-0 rounded-full transition-[height,opacity]',
            active && 'animate-pulse bg-red-500/70'
          )}
          style={{ height: bar.height }}
        />
      ))}
    </div>
  )
}

export const VoiceMemoWaveform = memo(VoiceMemoWaveformComponent)
VoiceMemoWaveform.displayName = 'VoiceMemoWaveform'
